'use client';

import React from 'react';
import { cn } from '@utils';
import {
	composeRenderProps,
	FieldErrorProps,
	Group,
	GroupProps,
	InputProps,
	LabelProps,
	FieldError as RACFieldError,
	Input as RACInput,
	Label as RACLabel,
	Text,
	TextProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import { ErrorFilled, SuccessFilled } from '../icons';

export type FieldWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type FieldVariant = 'outline' | 'filled';

export const iconStyle = tv({
	base: ['size-icon-sm my-[0.5px] flex-shrink-0'],
	variants: {
		theme: {
			light: ['text-light-action-secondary'],
			dark: ['text-dark-action-secondary'],
		},
		isInvalid: {
			true: 'text-status-error',
		},
		isValid: {
			true: 'text-status-success',
		},
		isDisabled: {
			true: 'cursor-not-allowed',
		},
		isFocusWithin: {
			true: '',
		},
	},
	compoundVariants: [
		{
			theme: 'light',
			isDisabled: true,
			className: 'text-light-text-lighter',
		},
		{
			theme: 'dark',
			isDisabled: true,
			className: 'text-dark-text-lighter',
		},
		{
			theme: 'light',
			isFocusWithin: true,
			className: 'text-light-action-secondary',
		},
		{
			theme: 'dark',
			isFocusWithin: true,
			className: 'text-dark-action-secondary',
		},
	],
	defaultVariants: {
		theme: 'light',
	},
});

export const errorMessageStyle = tv({
	base: 'flex items-start gap-1 text-xs font-normal',
	variants: {
		theme: {
			light: 'text-status-error',
			dark: 'text-status-error-lighter',
		},
	},
	defaultVariants: {
		theme: 'light',
	},
});

export function Label({
	isRequired,
	theme,
	children,
	...props
}: LabelProps & { isRequired?: boolean; theme?: 'light' | 'dark' }) {
	const labelStyle = tv({
		base: 'w-fit cursor-default text-sm leading-4 font-semibold',
		variants: {
			theme: {
				light: 'text-light-text',
				dark: 'text-dark-text',
			},
		},
		defaultVariants: {
			theme: 'light',
		},
	});

	const optionalTextStyle = tv({
		base: 'ml-1 text-xs font-normal',
		variants: {
			theme: {
				light: 'text-light-text-lighter',
				dark: 'text-dark-text',
			},
		},
		defaultVariants: {
			theme: 'light',
		},
	});

	return (
		<RACLabel {...props} className={cn(labelStyle({ theme }), props.className)}>
			{isRequired && <span className="text-status-error mr-1">*</span>}
			{children}
			{isRequired === false && <span className={optionalTextStyle({ theme })}>(Optional)</span>}
		</RACLabel>
	);
}

export function Description({ theme, ...props }: TextProps & { theme?: 'light' | 'dark' }) {
	const descriptionStyle = tv({
		base: 'text-xs font-normal',
		variants: {
			theme: {
				light: 'text-light-text-lighter',
				dark: 'text-dark-text-lighter',
			},
		},
		defaultVariants: {
			theme: 'light',
		},
	});
	return (
		<Text
			{...props}
			slot="description"
			className={cn(descriptionStyle({ theme }), props.className)}
		/>
	);
}

export function FieldSuccess({ theme, ...props }: TextProps & { theme?: 'light' | 'dark' }) {
	const successMessageStyle = tv({
		base: 'flex items-start gap-1 text-xs font-normal',
		variants: {
			theme: {
				light: 'text-light-text',
				dark: 'text-dark-text',
			},
		},
		defaultVariants: {
			theme: 'light',
		},
	});

	return (
		<Text
			{...props}
			slot="description"
			className={cn(successMessageStyle({ theme }), props.className)}
		>
			<SuccessFilled className={iconStyle({ theme, isValid: true })} />
			{/* React 19 safe - no .toString() needed */}
			{props.children as React.ReactNode}
		</Text>
	);
}

export function FieldError({ theme, ...props }: FieldErrorProps & { theme?: 'light' | 'dark' }) {
	return (
		<RACFieldError {...props} className={cn(errorMessageStyle({ theme }), props.className)}>
			<ErrorFilled className={iconStyle({ theme, isInvalid: true })} />
			{/* React 19 fix: Remove .toString() which caused Error #31 */}
			{props.children as React.ReactNode}
		</RACFieldError>
	);
}

export const fieldStyle = tv({
	base: [
		'cursor-text overflow-hidden rounded-xs',
		'group flex h-12 min-h-12 min-w-0 flex-row items-center justify-between gap-2 px-3 py-1.5',
	],
	variants: {
		width: {
			xs: 'max-w-form-xs',
			sm: 'max-w-form-sm',
			md: 'max-w-form-md',
			lg: 'max-w-form-lg',
			xl: 'max-w-form-xl',
			full: 'w-full',
		},
		variant: {
			outline: 'bg-neutral-white border-2',
			filled: 'bg-neutral-lightest rounded-b-none border-b-2',
		},
		theme: {
			light: [
				'text-light-text placeholder:text-light-text-lighter border-light-border-alt light-focus',
			],
			dark: [
				'bg-dark-background text-dark-text placeholder:text-dark-text-lighter border-dark-border-alt dark-focus',
			],
		},
		isDisabled: {
			true: 'cursor-not-allowed',
		},
		isInvalid: {
			true: 'border-status-error bg-status-error-lightest',
		},
		isValid: {
			true: 'border-status-success bg-status-success-lightest',
		},
		isFocusWithin: {
			true: 'outline-3',
		},
	},
	compoundVariants: [
		{
			theme: 'light',
			isDisabled: false,
			isInvalid: false,
			isValid: false,
			isFocusWithin: false,
			className: 'hover:border-light-action-primary-hover hover:bg-neutral-lightest',
		},
		{
			theme: 'dark',
			isDisabled: false,
			isInvalid: false,
			isValid: false,
			isFocusWithin: false,
			className: 'hover:border-dark-action-primary-hover hover:bg-dark-background-shade',
		},
		{
			theme: 'light',
			isDisabled: true,
			className:
				'border-light-border-alt bg-neutral-lightest text-light-text-lighter hover:border-light-border-alt opacity-60',
		},
		{
			theme: 'dark',
			isDisabled: true,
			className:
				'border-dark-border-alt bg-dark-background-shade text-dark-text-lighter hover:border-dark-border-alt opacity-80',
		},
		{
			theme: 'light',
			isFocusWithin: true,
			className:
				'bg-neutral-white outline-light-focus border-light-border-alt rounded-xs border-2 placeholder:text-transparent',
		},
		{
			theme: 'light',
			isFocusWithin: true,
			variant: 'filled',
			className: 'border-neutral-lighter',
		},
		{
			theme: 'dark',
			isFocusWithin: true,
			className:
				'bg-dark-background-shade outline-dark-focus border-neutral-lighter placeholder:text-transparent',
		},
	],
	defaultVariants: {
		variant: 'outline',
		theme: 'light',
		width: 'full',
	},
});

export function FieldGroup({
	theme,
	variant,
	isValid,
	width,
	...props
}: GroupProps & {
	theme?: 'light' | 'dark';
	isValid?: boolean;
	variant?: FieldVariant;
	width?: FieldWidth;
}) {
	return (
		<Group
			{...props}
			className={composeRenderProps(props.className, (className, renderProps) =>
				fieldStyle({ ...renderProps, theme, isValid, variant, width, className }),
			)}
		/>
	);
}

export function Input({ theme, ...props }: InputProps & { theme?: 'light' | 'dark' }) {
	const inputStyle = tv({
		base: 'w-full cursor-text outline-0 disabled:cursor-not-allowed',
		variants: {
			theme: {
				light: 'text-light-text placeholder:text-light-text-lighter',
				dark: 'text-dark-text placeholder:text-dark-text-lighter',
			},
		},
		defaultVariants: {
			theme: 'light',
		},
	});
	return <RACInput {...props} className={cn(inputStyle({ theme }), props.className)} />;
}
